/**
 * Adapter that exposes the vendored TheiaPhyloViewer SVG library
 * (`TheiaPhyloViewer`) through kronos2's runtime renderer contract.
 *
 * The library ships raw ESM with no type declarations; `allowJs`/`checkJs:false`
 * lets TypeScript resolve it without checking it. We insulate our own code from
 * whatever `tsc` infers by treating the library through the local structural
 * interfaces below.
 */
import { TheiaPhyloViewer } from '../../vendor/theiaphyloviewer.svg/src/index.js';
import { buildGraph } from '../../vendor/theiaphyloviewer.svg/src/core/index.js';
import { worldToClient } from '../../vendor/theiaphyloviewer.svg/src/render/camera.js';
import { hexToRgba } from './color-utils';
import { buildTooltipText } from './tooltip-text';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Per-leaf style as kronos2's color logic produces it (hex strings). */
export interface KronosNodeStyle {
  fillColor?: string;
  strokeColor?: string;
  shape?: string;
  size?: number;
  label?: string;
}

/** Per-leaf style as the library consumes it (British spelling, rgba arrays). */
export interface LibraryLeafStyle {
  shape?: string;
  size?: number;
  fillColour?: [number, number, number, number];
  strokeColour?: [number, number, number, number];
  label?: string;
}

/** A metadata cell as `TreeMetadataSync.computeMetadataBlocks` produces it. */
export interface MetadataCell {
  label: string;
  colour: string;
}

/** A metadata cell as the library consumes it. */
export interface LibraryMetadataCell {
  label: string;
  colour: [number, number, number, number];
}

/** Library viewBox window in world (tree-space) coordinates. */
export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Minimal structural view of a `TheiaPhyloViewer` instance (only what we call). */
export interface TheiaPhyloViewerInstance {
  setProps(next: Record<string, unknown>): unknown;
  /** Set the native hover-tooltip text (falsy clears it). */
  setTooltip(text: string): unknown;
  highlightNode(id: string | null): unknown;
  /** Display re-anchor: show only this node's subtree, or the whole tree for null. */
  setRoot(nodeOrId: string | null): unknown;
  /** Collapse an internal node to a single marker. No-op for leaves. */
  collapseNode(nodeOrId: string): unknown;
  /** Expand one collapsed node, or, with no argument, expand all. */
  expandNode(nodeOrId?: string | null): unknown;
  /** Flip child order at an internal node (cosmetic toggle). No-op for leaves. */
  rotateNode(nodeOrId: string): unknown;
  /** Structural re-root onto a node's branch. No-op for the root/leaves. */
  rerootNode(nodeOrId: string): unknown;
  /** Structural re-root at the midpoint of the longest leaf-to-leaf path. */
  midpointRoot(): unknown;
  /** Ladderise so each node's smaller child sits first (small→large). */
  ascendingNodeOrder(): unknown;
  /** Ladderise so each node's larger child sits first (large→small). */
  descendingNodeOrder(): unknown;
  /** Serialise the live (re-rooted/ladderised) topology to a Newick string. */
  exportNewick(options?: { digits?: number }): string;
  /**
   * Serialise the whole session, topology (loss-free Newick), props, view and
   * highlight: to a plain object suitable for `JSON.stringify`. Inverse of
   * `importJSON`.
   */
  exportJSON(): unknown;
  /** Restore a session produced by `exportJSON` (rebuilds the tree + props + view). */
  importJSON(session: unknown): unknown;
  /**
   * Create a clade annotation (a coloured box over the subtree rooted at
   * `input.nodeId`). The library merges `annotationDefaults`, so a bare
   * `{ nodeId }` still renders. Returns the created entry (carrying its id).
   */
  createAnnotation(input?: { nodeId: string; id?: string; [k: string]: unknown }): {
    id: string;
  };
  /** Delete a clade annotation by id. No-op for unknown ids. */
  deleteAnnotation(id: string): unknown;
  /**
   * Create a leaf-label annotation (a coloured band in the label gutter beside
   * `input.nodeId`). The library merges `leafLabelAnnotationDefaults`, so a bare
   * `{ nodeId }` still renders. Returns the created entry (carrying its id).
   */
  createLeafLabelAnnotation(input?: { nodeId: string; id?: string; [k: string]: unknown }): {
    id: string;
  };
  /** Delete a leaf-label annotation by id. No-op for unknown ids. */
  deleteLeafLabelAnnotation(id: string): unknown;
  /**
   * The graph rebuilt from the current `source`, used to re-sync the adapter's
   * node maps after a topology change (reroot regenerates internal ids).
   */
  getGraphAfterLayout(): {
    leaves?: Array<{ id: string }>;
    nodeById?: Record<string, GraphNode>;
    root?: { id: string };
  };
  /** Fit the view to the rendered tree (resets pan/zoom). */
  fitInCanvas(): unknown;
  /**
   * Look up a laid-out node by id. Returns the node (carrying world-space
   * coordinates `x`/`y`) or `null` before the first render / for an unknown id.
   */
  findNodeById(id: string): { x: number; y: number } | null;
  /** Current viewBox (world space). */
  getView(): ViewBox;
  /** Replace the viewBox outright (pan + zoom). */
  setView(viewBox: ViewBox): unknown;
  /** Current uniform zoom relative to the fitted scale (fit == 0, +1 == 2x in). */
  getZoom(): number;
  /** Set the uniform zoom relative to the fitted scale (keeps the centre). */
  setZoom(zoom: number): unknown;
  /** Independent branch-axis zoom exponent (`scale ∝ 2 ** z`); a persisted prop. */
  getBranchZoom(): number;
  setBranchZoom(zoom: number): unknown;
  /** Independent step-axis (leaf-spacing) zoom exponent; a persisted prop. */
  getStepZoom(): number;
  setStepZoom(zoom: number): unknown;
  exportSVG(): string;
  exportPNG(scale?: number): Promise<string>;
  destroy(): void;
}

/** Factory for a library instance, injected so the adapter is unit-testable. */
export type InstanceFactory = (
  container: HTMLElement,
  props: Record<string, unknown>,
) => TheiaPhyloViewerInstance;

/**
 * Minimal structural view of a `buildGraph` node, only the fields the adapter
 * reads. `children` holds node references (not ids), matching the library's
 * graph shape (`core/graph-stages.js`).
 */
export interface GraphNode {
  id: string;
  isLeaf: boolean;
  children?: GraphNode[];
}

/** Minimal structural view of the pure `buildGraph` result. */
interface BuiltGraph {
  leaves?: Array<{ id: string }>;
  nodeById?: Record<string, GraphNode>;
  root?: { id: string };
}

/**
 * Collect the leaf ids of the subtree rooted at `nodeId`, in DFS (left-to-right)
 * order. A leaf id resolves to itself; an unknown id yields `[]`. Pure, the
 * decision heart of the subtree-view feature, so it is unit-tested without a DOM.
 */
export function subtreeLeafIds(
  nodeById: Record<string, GraphNode>,
  nodeId: string,
): string[] {
  const start = nodeById[nodeId];
  if (!start) return [];
  const out: string[] = [];
  const walk = (node: GraphNode): void => {
    if (node.isLeaf) {
      out.push(node.id);
      return;
    }
    for (const child of node.children ?? []) walk(child);
  };
  walk(start);
  return out;
}

/** The node a right-click landed on, or `null` for empty canvas. */
export interface MenuTarget {
  id: string;
  isInternal: boolean;
  /** True when this internal node is currently collapsed (offers Expand). */
  isCollapsed?: boolean;
  /** True for the current root, suppresses "Re-root here" (the library no-ops). */
  isRoot?: boolean;
  /** True when this internal node already carries a clade annotation (offers Remove). */
  hasAnnotation?: boolean;
  /** True when this leaf already carries a label-gutter band (offers Remove). */
  hasLabelBand?: boolean;
}

/** A context-menu entry: a stable action `id` and its display `label`. */
export interface SubtreeMenuItem {
  id:
    | 'view-subtree'
    | 'collapse'
    | 'expand'
    | 'rotate'
    | 'reroot'
    | 'annotate-clade'
    | 'remove-annotation'
    | 'annotate-leaf'
    | 'remove-leaf-band'
    | 'midpoint-root'
    | 'ladderise-asc'
    | 'ladderise-desc'
    | 'export-newick'
    | 'save-session'
    | 'load-session'
    | 'lasso-select'
    | 'box-select'
    | 'expand-all'
    | 'reset-view';
  label: string;
}

/**
 * Flags that gate the global (no-node-target) context-menu actions and the
 * tree-wide affordances. Grouped into an options object because the per-node
 * verbs key off `target`, while these are tree-state booleans that have grown
 * past the point where positional args stay readable.
 */
export interface SubtreeMenuOptions {
  /** A display subtree is active, offer "Reset view". */
  subtreeActive?: boolean;
  /** Some node is collapsed, offer "Expand all". */
  anyCollapsed?: boolean;
  /** Tree has >=2 leaves, offer the global "Midpoint root". */
  canMidpointRoot?: boolean;
  /** Tree has >=2 leaves, offer the global "Ladderise ascending/descending". */
  canLadderise?: boolean;
  /** A tree is loaded, offer the global "Export Newick". */
  canExport?: boolean;
  /** A tree is loaded, offer the global "Save session" / "Load session". */
  canSession?: boolean;
  /** A tree is loaded, offer the global "Lasso select" / "Box select". */
  canSelect?: boolean;
}

/**
 * Decide which context-menu entries to show. Pure so the interaction policy is
 * unit-tested without a DOM. Over an internal node: "View subtree", then either
 * "Collapse" (expanded) or "Expand" (collapsed), then "Rotate", then "Re-root
 * here" (suppressed on the root, where the library no-ops), then either
 * "Annotate clade" or "Remove annotation" (whichever the node's annotation
 * state calls for). Over a leaf: "Annotate label" or "Remove label band"
 * (whichever the leaf's label-gutter state calls for). Then the global
 * actions (no node target) appear whenever their gate is set, "Midpoint root"
 * and the two "Ladderise" verbs (structural), then the output actions "Export
 * Newick", "Save session" and "Load session", so they surface the menu over
 * leaves and empty canvas too. "Expand all" appears
 * whenever any node is collapsed, and "Reset view" whenever a subtree is active.
 * An empty result means no menu (the default browser menu is left alone).
 */
export function subtreeMenuItems(
  target: MenuTarget | null,
  opts: SubtreeMenuOptions = {},
): SubtreeMenuItem[] {
  const {
    subtreeActive = false,
    anyCollapsed = false,
    canMidpointRoot = false,
    canLadderise = false,
    canExport = false,
    canSession = false,
    canSelect = false,
  } = opts;
  const items: SubtreeMenuItem[] = [];
  if (target && target.isInternal) {
    items.push({ id: 'view-subtree', label: 'View subtree' });
    items.push(
      target.isCollapsed
        ? { id: 'expand', label: 'Expand' }
        : { id: 'collapse', label: 'Collapse' },
    );
    items.push({ id: 'rotate', label: 'Rotate' });
    if (!target.isRoot) items.push({ id: 'reroot', label: 'Re-root here' });
    items.push(
      target.hasAnnotation
        ? { id: 'remove-annotation', label: 'Remove annotation' }
        : { id: 'annotate-clade', label: 'Annotate clade' },
    );
  }
  if (target && !target.isInternal) {
    items.push(
      target.hasLabelBand
        ? { id: 'remove-leaf-band', label: 'Remove label band' }
        : { id: 'annotate-leaf', label: 'Annotate label' },
    );
  }
  if (canMidpointRoot) items.push({ id: 'midpoint-root', label: 'Midpoint root' });
  if (canLadderise) {
    items.push({ id: 'ladderise-asc', label: 'Ladderise ascending' });
    items.push({ id: 'ladderise-desc', label: 'Ladderise descending' });
  }
  if (canExport) items.push({ id: 'export-newick', label: 'Export Newick' });
  if (canSession) {
    items.push({ id: 'save-session', label: 'Save session' });
    items.push({ id: 'load-session', label: 'Load session' });
  }
  if (canSelect) {
    items.push({ id: 'lasso-select', label: 'Lasso select' });
    items.push({ id: 'box-select', label: 'Box select' });
  }
  if (anyCollapsed) items.push({ id: 'expand-all', label: 'Expand all' });
  if (subtreeActive) items.push({ id: 'reset-view', label: 'Reset view' });
  return items;
}

// ---------------------------------------------------------------------------
// Selection geometry (no DOM, unit-testable in the `node` env)
//
// Lasso/box hit-testing runs entirely in client/screen space: the gesture
// points are captured as clientX/clientY and each tip's world position is
// projected to client space (worldToClient) before the test, so "inside the
// drawn shape" means inside as the user sees it on screen.
// ---------------------------------------------------------------------------

/** A point in client (screen-pixel) space. */
export interface Point2D {
  x: number;
  y: number;
}

/** An axis-aligned rectangle with non-negative extent. */
export interface SelectionRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A tip projected to client space, tagged with its leaf id. */
export interface TipPoint {
  id: string;
  x: number;
  y: number;
}

/** A completed selection gesture: a freehand polygon or an axis-aligned box. */
export type SelectionShape =
  | { kind: 'lasso'; points: readonly Point2D[] }
  | { kind: 'box'; rect: SelectionRect };

/**
 * Ray-casting point-in-polygon test. Counts how many polygon edges a ray cast
 * to the right from `pt` crosses; an odd count means inside. Handles concave
 * polygons. Ported from the phylocanvas lasso, kept pure.
 */
export function pointInPolygon(pt: Point2D, polygon: readonly Point2D[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (!a || !b) continue;
    const intersects =
      a.y > pt.y !== b.y > pt.y &&
      pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Build a rectangle with non-negative width/height from any two drag corners,
 * so a drag in any direction (including up-left) yields the same box.
 */
export function normalizeRect(a: Point2D, b: Point2D): SelectionRect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

/** Inclusive containment test for a normalized rectangle. */
export function pointInRect(pt: Point2D, rect: SelectionRect): boolean {
  return (
    pt.x >= rect.x && pt.x <= rect.x + rect.w && pt.y >= rect.y && pt.y <= rect.y + rect.h
  );
}

/** Leaf ids of the tips enclosed by a lasso polygon or a box. */
export function tipsInShape(
  tips: readonly TipPoint[],
  shape: SelectionShape,
): string[] {
  const encloses =
    shape.kind === 'lasso'
      ? (t: TipPoint) => pointInPolygon(t, shape.points)
      : (t: TipPoint) => pointInRect(t, shape.rect);
  // A tip may contribute several hit points (branch end + label), so dedupe.
  return [...new Set(tips.filter(encloses).map((t) => t.id))];
}

/**
 * Combine a freshly enclosed set with the current selection: replace by default,
 * or union (deduped) when the gesture was additive (Ctrl/Cmd held).
 */
export function mergeSelection(
  current: readonly string[],
  hits: readonly string[],
  additive: boolean,
): string[] {
  return additive ? [...new Set([...current, ...hits])] : [...hits];
}

// ---------------------------------------------------------------------------
// Pure translation functions (no DOM, unit-testable in the `node` env)
// ---------------------------------------------------------------------------

/** kronos2 layout name → library `type` code. */
export const layoutMap: Record<string, string> = {
  rectangular: 'rc',
  circular: 'cr',
  radial: 'rd',
  diagonal: 'dg',
  hierarchical: 'hr',
};

/** Resolve a kronos2 layout name to a library type code (default `rc`). */
export function resolveLayout(layoutName?: string): string {
  return (layoutName != null && layoutMap[layoutName]) || 'rc';
}

/**
 * Distinct, semi-transparent fills cycled across successive clade annotations so
 * stacked boxes stay visually separable. RGBA in the library's `[r,g,b,a]` (0–255)
 * convention; the alpha keeps the underlying tree readable. The library's grey
 * `annotationDefaults` would render every box identically, so the adapter assigns
 * a palette colour per annotation instead.
 */
export const CLADE_ANNOTATION_PALETTE: ReadonlyArray<readonly [number, number, number, number]> = [
  [31, 119, 180, 90],
  [255, 127, 14, 90],
  [44, 160, 44, 90],
  [214, 39, 40, 90],
  [148, 103, 189, 90],
  [140, 86, 75, 90],
];

/** Pick the annotation fill for the nth annotation, cycling the palette. */
export function cladeAnnotationColour(index: number): readonly [number, number, number, number] {
  return CLADE_ANNOTATION_PALETTE[index % CLADE_ANNOTATION_PALETTE.length]!;
}

/** Convert kronos2 hex-string node styles into library leaf styles. */
export function toLibraryStyles(
  styles: Record<string, KronosNodeStyle>,
): Record<string, LibraryLeafStyle> {
  const out: Record<string, LibraryLeafStyle> = {};
  for (const [id, s] of Object.entries(styles)) {
    const ls: LibraryLeafStyle = {};
    if (s.shape != null) ls.shape = s.shape;
    if (s.size != null) ls.size = s.size;
    if (s.fillColor != null) ls.fillColour = hexToRgba(s.fillColor);
    if (s.strokeColor != null) ls.strokeColour = hexToRgba(s.strokeColor);
    if (s.label != null) ls.label = s.label;
    out[id] = ls;
  }
  return out;
}

/**
 * Reshape a colour-by `leaf id → hex` map (as produced by
 * `TreeMetadataSync.generateLeafColorMap`) into per-leaf library styles carrying
 * only a `fillColour`. `null`/empty yields `{}` (colour-by cleared).
 */
export function toColorByStyles(
  map: Record<string, string> | null | undefined,
): Record<string, LibraryLeafStyle> {
  const out: Record<string, LibraryLeafStyle> = {};
  if (!map) return out;
  for (const [id, hex] of Object.entries(map)) {
    out[id] = { fillColour: hexToRgba(hex) };
  }
  return out;
}

/** Reshape `computeMetadataBlocks` output into the library's metadata prop. */
export function toLibraryMetadata(
  meta: Record<string, Record<string, MetadataCell>>,
): Record<string, Record<string, LibraryMetadataCell>> {
  const out: Record<string, Record<string, LibraryMetadataCell>> = {};
  for (const [leaf, cols] of Object.entries(meta)) {
    out[leaf] = {};
    for (const [col, cell] of Object.entries(cols)) {
      out[leaf]![col] = { label: cell.label, colour: hexToRgba(cell.colour) };
    }
  }
  return out;
}

/**
 * Compute the display styles for a search filter, non-destructively: leaves not
 * in `keep` are hidden (no shape, transparent, blank label); everything else
 * keeps its base style. `keep` null/empty means "show all".
 */
export function filteredStyleOverrides(
  base: Record<string, LibraryLeafStyle>,
  leafIds: Set<string>,
  keep: readonly string[] | null,
): Record<string, LibraryLeafStyle> {
  if (!keep || keep.length === 0) return { ...base };
  const keepSet = new Set(keep);
  const out: Record<string, LibraryLeafStyle> = { ...base };
  for (const id of leafIds) {
    if (!keepSet.has(id)) {
      out[id] = {
        ...(out[id] ?? {}),
        shape: 'none',
        fillColour: [0, 0, 0, 0],
        strokeColour: [0, 0, 0, 0],
        label: '',
      };
    }
  }
  return out;
}

/**
 * Convert the dialog's hyphenated corner string ('bottom-left', …) into the
 * library's scalebar-position offset object. The library currently honours only
 * `.left`/`.bottom` (`render/scalebar.js`), so non-bottom-left corners render
 * bottom-left today, but the object is emitted with the correct corner keys so
 * it works unchanged should the library gain `.top`/`.right` support. Unknown
 * values fall back to bottom-left (the library default).
 */
function scalebarPositionToObject(value: unknown): Record<string, number> {
  switch (value) {
    case 'bottom-right':
      return { bottom: 0, right: 0 };
    case 'top-left':
      return { top: 0, left: 0 };
    case 'top-right':
      return { top: 0, right: 0 };
    case 'bottom-left':
    default:
      return { bottom: 0, left: 0 };
  }
}

/**
 * Translate the Tableau dialog's `phylocanvas` settings bag (the schema written
 * by `tree-settings-tabbed-dialog.ts` `saveSettingsLive`) into TheiaPhyloViewer
 * library props. Pure and additive: a prop is emitted only when its source key
 * is present, so the result can be merged via `setProps` without ever clobbering
 * a library default with `undefined`. Prop names are verified against
 * `vendor/.../src/core/defaults.js`; the label/edge/border semantics mirror the
 * phylocanvas renderer so both renderers honour the same controls identically.
 */
export function mapPhylocanvasSettingsToProps(
  phylo: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const has = (key: string): boolean => phylo[key] !== undefined;

  // Layout: the dialog already stores TreeTypes codes ('rc'/'cr'/'rd'/'dg'/'hr').
  if (has('treeLayout')) out.type = phylo.treeLayout;

  // Sizes (numbers, passed through unchanged).
  if (has('leafNodeSize')) out.nodeSize = phylo.leafNodeSize;
  if (has('labelSize')) out.fontSize = phylo.labelSize;

  // Leaf/internal toggles. Prefer the explicit `showLeafLabels`/`showInternalLabels`
  // the phylocanvas renderer writes; fall back to the dialog/panel spellings
  // (`showLabels` as the leaf toggle, `internalLabels`). The library gates both
  // behind a master `showLabels` (render/labels.js), derived as (leaf OR internal).
  const hasLeaf = has('showLeafLabels') || has('showLabels');
  const leaf = has('showLeafLabels') ? Boolean(phylo.showLeafLabels) : Boolean(phylo.showLabels);
  const hasInternal = has('showInternalLabels') || has('internalLabels');
  const internal = has('showInternalLabels')
    ? Boolean(phylo.showInternalLabels)
    : Boolean(phylo.internalLabels);
  if (hasLeaf) out.showLeafLabels = leaf;
  if (hasInternal) out.showInternalLabels = internal;
  if (hasLeaf || hasInternal) {
    out.showLabels = (hasLeaf && leaf) || (hasInternal && internal);
  }

  if (has('alignLabels')) out.alignLabels = Boolean(phylo.alignLabels);

  // Edge colouring. The dialog writes both spellings; mirror phylocanvas's
  // precedence (colourInternalEdges wins, else colourEdges).
  if (has('colourInternalEdges')) out.styleNodeEdges = Boolean(phylo.colourInternalEdges);
  else if (has('colourEdges')) out.styleNodeEdges = Boolean(phylo.colourEdges);

  // Leaf borders. Same alias precedence (showLeafBorders wins, else leafBorders);
  // drive shapeBorderWidth alongside, mirroring phylocanvas-renderer.ts.
  if (has('showLeafBorders') || has('leafBorders')) {
    const borders = has('showLeafBorders')
      ? Boolean(phylo.showLeafBorders)
      : Boolean(phylo.leafBorders);
    out.showShapeBorders = borders;
    out.shapeBorderWidth = borders ? 1 : 0;
  }

  // Scalebar.
  if (has('scalebarVisible')) out.scalebar = Boolean(phylo.scalebarVisible);
  if (has('scalebarWidth')) out.scalebarWidth = phylo.scalebarWidth;
  if (has('scalebarPosition')) {
    out.scalebarPosition = scalebarPositionToObject(phylo.scalebarPosition);
  }

  // Branch-length precision. The dialog/panel store a digit count; the library
  // formats each label via branchLengthsFormat ('decimal') + branchLengthsDigits
  // (render/branch-labels.js). Routing it through the mapper means a persisted
  // decimalDigits also restores via applyLoadedSettings, not just live edits.
  if (has('decimalDigits')) {
    out.branchLengthsFormat = 'decimal';
    out.branchLengthsDigits = phylo.decimalDigits;
  }

  // `solidEdges` (dialog toggle) has no library equivalent and was already a
  // no-op in phylocanvas, intentionally dropped.

  return out;
}

/** Default factory: the real library class, viewed through our interface. */
const defaultCreateInstance: InstanceFactory = (container, props) =>
  new TheiaPhyloViewer(container, props) as unknown as TheiaPhyloViewerInstance;

// ---------------------------------------------------------------------------
// Brand watermark
// ---------------------------------------------------------------------------

/**
 * A faded Theiagen Genomics mark pinned to the bottom-right of the tree panel.
 * It lives as a sibling of the library's <svg> inside the container: the library
 * only ever mutates its own <svg>, so the overlay survives every `setProps`, and
 * the app's `container.innerHTML = ''` on renderer switch tears it down for free.
 * `pointer-events: none` keeps tree pan/zoom/click reaching the SVG underneath,
 * and `exportSVG()` (which serializes only the <svg>) stays watermark-free.
 */
const WATERMARK_SRC = 'img/theia-logo.png';
const WATERMARK_OPACITY = '0.35';
const WATERMARK_WIDTH = '105px';

// ---------------------------------------------------------------------------
// Adapter class
// ---------------------------------------------------------------------------

type Listener = (data: unknown) => void;

export class TheiaPhyloViewerRenderer {
  private readonly container: HTMLElement;
  private readonly newick: string;
  private readonly app: unknown;
  private readonly createInstance: InstanceFactory;

  private instance: TheiaPhyloViewerInstance | null = null;
  private readonly listeners: Record<string, Listener[]> = {};
  // Not readonly: a structural reroot re-hangs the tree and regenerates internal
  // ids, so these maps are rebuilt from the library's graph after a reroot.
  private leafIds: Set<string>;
  private nodeById: Record<string, GraphNode>;
  /** Id of the current root, gates "Re-root here" and is refreshed on reroot. */
  private rootId: string | null;
  private subtreeActive = false;
  /** Ids of internal nodes the user has collapsed (drives the menu label). */
  private readonly collapsedIds = new Set<string>();
  /**
   * Internal-node ids that currently carry a clade annotation, the adapter's
   * mirror of the library's `annotations` prop, so the context menu can offer
   * "Annotate clade" vs "Remove annotation" without reading library internals.
   * Kept in sync on create/remove and rebuilt from a restored session.
   */
  private readonly annotatedNodeIds = new Set<string>();

  /**
   * Local mirror of which leaves carry a label-gutter band, a Set view of the
   * library's `leafLabelAnnotations` prop, so the context menu can offer
   * "Annotate label" vs "Remove label band" without reading library internals.
   * Kept in sync on create/remove and rebuilt from a restored session.
   */
  private readonly labelBandLeafIds = new Set<string>();

  private selectedIds: string[] = [];
  private baseStyles: Record<string, LibraryLeafStyle> = {};
  private colorByStyles: Record<string, LibraryLeafStyle> = {};
  private labelOverrides: Record<string, string> = {};
  private filteredTips: string[] | null = null;
  private onContainerClick: ((e: Event) => void) | null = null;
  private onContainerContextMenu: ((e: Event) => void) | null = null;
  private onContainerMouseMove: ((e: Event) => void) | null = null;
  private onDocMouseDown: ((e: Event) => void) | null = null;
  // The block cells last handed to `setMetadataBlocks`, by leaf id; the hover
  // tooltip reads each leaf's values straight from here.
  private blockMetadata: Record<string, Record<string, MetadataCell>> = {};
  // Which metadata fields the tooltip shows; empty means show every value.
  private tooltipFields: string[] = [];
  private contextMenuEl: HTMLElement | null = null;
  private watermarkEl: HTMLImageElement | null = null;
  private loadedPhylocanvasSettings: Record<string, unknown> | null = null;

  // ---- Lasso / box selection -----------------------------------------------
  // `pan` lets pointer events fall through to the library (pan/zoom/click);
  // `lasso`/`box` make the overlay capture the drag, suspending pan. The overlay
  // is renderer-owned chrome layered over the library's svg; the mode is armed
  // from the right-click context menu.
  private selectMode: 'pan' | 'lasso' | 'box' = 'pan';
  private selectOverlayEl: HTMLElement | null = null;
  private selectFeedbackEl: SVGElement | null = null;
  private selectDrawing = false;
  private selectAdditive = false;
  private selectPoints: Point2D[] = [];
  private onSelectPointerDown: ((e: PointerEvent) => void) | null = null;
  private onSelectPointerMove: ((e: PointerEvent) => void) | null = null;
  private onSelectPointerUp: ((e: PointerEvent) => void) | null = null;
  private onSelectKeyDown: ((e: KeyboardEvent) => void) | null = null;

  // Label-visibility state. The library gates leaf/internal labels behind a
  // master `showLabels` (see `labelVisibilityProps`), so the adapter tracks each
  // toggle independently and re-derives the master gate from both, the panel's
  // leaf/internal toggles must not clobber each other.
  private leafLabelsVisible = true;
  private internalLabelsVisible = false;

  // Tip labels are aligned to a common edge by default, the renderer's own
  // default, mirroring phylocanvas-renderer. The library defaults this to false
  // (core/defaults.js); we override it here so the out-of-the-box SVG render
  // lines labels up without the appearance panel having to push the setting
  // (the panel leaves an un-customised renderer at its own defaults).
  private labelsAligned = true;

  // The user's explicit "Leaf borders" preference (panel toggle / persisted
  // setting). The *effective* border is `userLeafBorders || colour-by active`:
  // the vendored library paints one shared border colour for every marker (it
  // never reads per-node strokeColour for shapes), so when colour-by is on we
  // force the global outline on to keep adjacent same-fill tips legible. See
  // `applyLeafBorders`.
  private userLeafBorders = false;

  /** Read by app.ts metadata path; a defined boolean keeps that code well-behaved. */
  hideNullBlocks = false;

  /**
   * Branch-length decimal places. Initialised to 1 for phylocanvas parity so the
   * renderer-agnostic Tableau SettingsChanged handler can read it and short-circuit
   * (it compares `renderer.decimalDigits` before calling `setDecimalDigits`).
   */
  decimalDigits = 1;

  constructor(
    container: HTMLElement,
    newick: string,
    app: unknown = null,
    createInstance: InstanceFactory = defaultCreateInstance,
  ) {
    this.container = container;
    this.newick = newick;
    this.app = app;
    this.createInstance = createInstance;

    const graph = buildGraph(newick, {}) as unknown as BuiltGraph;
    this.leafIds = new Set((graph.leaves ?? []).map((n) => n.id));
    this.nodeById = graph.nodeById ?? {};
    this.rootId = graph.root?.id ?? null;
  }

  render(): void {
    if (!this.instance) {
      this.instance = this.createInstance(this.container, this.buildInitialProps());
      this.attachClickListener();
      this.attachContextMenuListener();
      this.attachTooltipListener();
      this.mountSelectionUI();
      this.mountWatermark();
    } else {
      this.instance.setProps({});
    }
  }

  resize(): void {
    this.instance?.setProps({
      size: { width: this.container.clientWidth, height: this.container.clientHeight },
    });
  }

  /**
   * Store a Tableau settings bag for later application. Mirrors the phylocanvas
   * renderer's contract so app-tableau's guarded
   * `renderer.loadSettings({ phylocanvas })` lights up for the SVG renderer too.
   * Does not render, pair with `applyLoadedSettings()`.
   */
  loadSettings(settings: { phylocanvas?: Record<string, unknown> } | null | undefined): void {
    if (!settings || !settings.phylocanvas) return;
    this.loadedPhylocanvasSettings = settings.phylocanvas;
  }

  /** Apply the settings captured by `loadSettings()` to the live instance. */
  applyLoadedSettings(): void {
    if (!this.instance || !this.loadedPhylocanvasSettings) return;
    const settings = this.loadedPhylocanvasSettings;
    if ('showLeafBorders' in settings) this.userLeafBorders = Boolean(settings.showLeafBorders);
    else if ('leafBorders' in settings) this.userLeafBorders = Boolean(settings.leafBorders);
    this.instance.setProps(mapPhylocanvasSettingsToProps(settings));
    this.applyLeafBorders();
  }

  setNodeStyles(styles: Record<string, KronosNodeStyle>): void {
    this.baseStyles = toLibraryStyles(styles);
    this.pushStyles();
  }

  /**
   * Colour leaf markers by a precomputed `leaf id → hex` map (parity with
   * phylocanvas's "Color By"). `null`/empty clears colour-by and restores base
   * fills. Merges over base styles but under the search filter (see
   * `computeDisplayStyles`), so a hidden leaf stays transparent.
   */
  setLeafColorMap(map: Record<string, string> | null): void {
    this.colorByStyles = toColorByStyles(map);
    this.applyLeafBorders();
    this.pushStyles();
  }

  setSelectedIds(ids: readonly string[]): void {
    this.selectedIds = [...ids];
    this.instance?.setProps({ selectedIds: this.selectedIds });
  }

  setHoveredIds(ids: readonly string[]): void {
    this.instance?.highlightNode(ids[0] ?? null);
  }

  setFilteredTips(tips: readonly string[] | null): void {
    this.filteredTips = tips && tips.length > 0 ? [...tips] : null;
    this.pushStyles();
  }

  updateTipLabels(map: Record<string, string> | null): void {
    this.labelOverrides = map ?? {};
    this.pushStyles();
  }

  setMetadataBlocks(
    fields: string[],
    meta: Record<string, Record<string, MetadataCell>>,
  ): void {
    if (!fields || fields.length === 0) {
      this.blockMetadata = {};
      this.instance?.setProps({ metadata: {}, blocks: [] });
      return;
    }
    this.blockMetadata = meta;
    this.instance?.setProps({
      metadata: toLibraryMetadata(meta),
      blocks: [...fields],
      showBlockHeaders: true,
    });
  }

  /**
   * Choose which metadata values the hover tooltip shows (empty = every value).
   * Additive parity with phylocanvas's `selectedTooltipFields`; the tooltip text
   * is composed on hover from the blocks already set via `setMetadataBlocks`.
   */
  setTooltipFields(fields: string[]): void {
    this.tooltipFields = Array.isArray(fields) ? [...fields] : [];
  }

  toggleBranchLengths(show: boolean): void {
    this.instance?.setProps({ showBranchLengths: show });
  }

  // ---- subtree-view navigation --------------------------------------------
  //
  // Parity with phylocanvas's context-menu "View subtree" / "Reset view". The
  // app already listens for `subtreeView {leafIds}` and `resetView` on whatever
  // renderer is active (app.ts / app-tableau.ts) and filters the table/dashboard
  // accordingly: this emits the same events the SVG renderer never did. The
  // library's `setRoot` is a *display* re-anchor (reversible, distance-
  // preserving), not a structural reroot.

  /**
   * Re-anchor the display to the subtree rooted at `nodeId` and announce its
   * leaves via `subtreeView`. No-op for an unknown id (the menu only offers this
   * over real internal nodes, but guard anyway).
   */
  viewSubtree(nodeId: string): void {
    const leafIds = subtreeLeafIds(this.nodeById, nodeId);
    if (leafIds.length === 0) return;
    this.instance?.setRoot(nodeId);
    this.subtreeActive = true;
    this.emit('subtreeView', { leafIds });
  }

  /** Restore the whole tree, refit the view, and announce `resetView`. */
  resetSubtreeView(): void {
    this.instance?.setRoot(null);
    this.instance?.fitInCanvas();
    this.subtreeActive = false;
    this.emit('resetView', undefined);
  }

  // ---- collapse / expand --------------------------------------------------
  //
  // Parity with phylocanvas's "Collapse subtree". The library renders
  // `collapsedIds` already; these methods drive that prop through its own
  // `collapseNode`/`expandNode` and mirror the set locally so the context menu
  // knows whether to offer Collapse or Expand without reading library internals.

  /** Collapse the subtree at `nodeId` to a marker. No-op for leaves/unknown ids. */
  collapseNode(nodeId: string): void {
    if (!this.instance || this.nodeById[nodeId]?.isLeaf !== false) return;
    this.instance.collapseNode(nodeId);
    this.collapsedIds.add(nodeId);
  }

  /** Expand one collapsed node, or, with no argument, expand all. */
  expandNode(nodeId?: string): void {
    if (!this.instance) return;
    if (nodeId == null) {
      this.instance.expandNode();
      this.collapsedIds.clear();
      return;
    }
    this.instance.expandNode(nodeId);
    this.collapsedIds.delete(nodeId);
  }

  /** Whether `nodeId` is currently collapsed (used by the context menu + tests). */
  isCollapsed(nodeId: string): boolean {
    return this.collapsedIds.has(nodeId);
  }

  /** Whether any node is currently collapsed (gates the "Expand all" entry). */
  hasCollapsed(): boolean {
    return this.collapsedIds.size > 0;
  }

  /**
   * Flip the child order at internal node `nodeId` (cosmetic). The library's
   * `rotateNode` is a toggle, calling twice restores the order, and rotation
   * is distance-preserving, so it never refits. No-op for leaves/unknown ids or
   * before the first render. No local mirror needed: the menu label is always
   * "Rotate", so there is no Collapse-vs-Expand decision to shadow.
   */
  rotateNode(nodeId: string): void {
    if (!this.instance || this.nodeById[nodeId]?.isLeaf !== false) return;
    this.instance.rotateNode(nodeId);
  }

  /**
   * Structural re-root: drop a new root onto `nodeId`'s branch and re-hang the
   * tree. Unlike `viewSubtree` (a reversible *display* re-anchor), this rewrites
   * the topology, distance-preserving but permanent until undone by another
   * reroot. The library resets collapse state and clears any display anchor, so
   * the adapter mirrors that: it clears the collapsed set, drops `subtreeActive`,
   * and announces `resetView` to release any clade filter the app was holding.
   *
   * The re-hang regenerates internal node ids (leaf ids survive, see the
   * library's `core/reroot.js`), so the adapter re-reads the rebuilt graph to
   * keep `nodeById`/`leafIds`/`rootId` valid for subsequent menu actions.
   *
   * No-op for leaves, unknown ids, the root (the library no-ops there), or
   * before the first render.
   */
  rerootNode(nodeId: string): void {
    if (!this.instance || this.nodeById[nodeId]?.isLeaf !== false) return;
    if (nodeId === this.rootId) return;
    this.instance.rerootNode(nodeId);
    this.afterStructuralRehang();
  }

  /**
   * Structural re-root at the midpoint of the tree's longest leaf-to-leaf path.
   * A global action, no node target, so it lights up the context menu over
   * leaves and empty canvas too. Shares `rerootNode`'s state fan-out (it is the
   * same kind of re-hang, just auto-targeted). No-op for trees with fewer than
   * two leaves (matching the library) or before the first render.
   */
  midpointRoot(): void {
    if (!this.instance || this.leafIds.size < 2) return;
    this.instance.midpointRoot();
    this.afterStructuralRehang();
  }

  /**
   * Ladderise so each node's smaller child sits first (small→large). A global
   * action, like `midpointRoot`. The library re-hangs the tree from a reordered
   * `source` (it resets collapse/anchor and refits exactly as reroot does), so
   * the leaf set survives but internal ids regenerate; hence the shared
   * `afterStructuralRehang` fan-out. No-op below two leaves or before render.
   */
  ladderiseAscending(): void {
    if (!this.instance || this.leafIds.size < 2) return;
    this.instance.ascendingNodeOrder();
    this.afterStructuralRehang();
  }

  /** Ladderise so each node's larger child sits first (large→small). Mirror of
   * `ladderiseAscending`. */
  ladderiseDescending(): void {
    if (!this.instance || this.leafIds.size < 2) return;
    this.instance.descendingNodeOrder();
    this.afterStructuralRehang();
  }

  /**
   * Shared bookkeeping after a structural re-hang (`rerootNode`/`midpointRoot`/
   * the ladderise actions): the library resets collapse state and clears any
   * display anchor, so mirror that locally, re-sync the node maps to the re-hung
   * tree (internal ids regenerate), and release any active clade filter the app
   * was holding.
   */
  private afterStructuralRehang(): void {
    const hadSubtree = this.subtreeActive;
    this.collapsedIds.clear();
    this.subtreeActive = false;
    this.resyncGraphMaps();
    if (hadSubtree) this.emit('resetView', undefined);
  }

  /**
   * Rebuild `leafIds`/`nodeById`/`rootId` from the library's current graph.
   * Called after a topology change (reroot) so the adapter's view of node
   * identity tracks the re-hung tree. Safe no-op before the first render.
   */
  private resyncGraphMaps(): void {
    const graph = this.instance?.getGraphAfterLayout();
    if (!graph) return;
    this.leafIds = new Set((graph.leaves ?? []).map((n) => n.id));
    this.nodeById = graph.nodeById ?? {};
    this.rootId = graph.root?.id ?? null;
  }

  /**
   * Pan the view to centre the given tip, preserving the current zoom. Lights up
   * the app's feature-detected `treeRenderer.zoomToTip` channel (app.ts) so a
   * single-row table select reveals the matching tip, phylocanvas parity.
   *
   * The node's world-space coordinates (`findNodeById`) share the viewBox's
   * coordinate space (`getView`), so re-centring is pure world arithmetic: shift
   * the viewBox origin to put the tip at its centre while keeping width/height
   * (the zoom). Safe no-op before the first render, for an unknown id, or for
   * non-finite coordinates.
   */
  zoomToTip(leafId: string): void {
    const instance = this.instance;
    if (!instance) return;
    const node = instance.findNodeById(leafId);
    if (!node || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
    const view = instance.getView();
    instance.setView({
      x: node.x - view.w / 2,
      y: node.y - view.h / 2,
      w: view.w,
      h: view.h,
    });
  }

  /**
   * Current uniform zoom relative to the fitted scale (fit == 0, +1 == 2x in)-* the read-out for the appearance panel's zoom slider. Returns 0 (fit) before
   * the first render, so the slider sits at its neutral position.
   */
  getZoom(): number {
    return this.instance?.getZoom() ?? 0;
  }

  /**
   * Set the uniform zoom relative to the fitted scale, keeping the current
   * centre (the library zooms about the viewBox centre). Drives the appearance
   * panel's zoom slider. Transient view-state, deliberately not persisted, like
   * pan. Safe no-op before the first render.
   */
  setZoom(zoom: number): void {
    this.instance?.setZoom(zoom);
  }

  /**
   * Independent branch-axis zoom, stretches the tree along the branch-length
   * axis (x for most layouts, y for hierarchical). Unlike uniform zoom this is a
   * persisted *prop* (the library's `setBranchZoom` calls `setProps`), so it
   * round-trips through the appearance panel like the size sliders. Returns 0
   * (no stretch) before the first render. Safe no-op when not yet rendered.
   */
  getBranchZoom(): number {
    return this.instance?.getBranchZoom() ?? 0;
  }

  setBranchZoom(zoom: number): void {
    this.instance?.setBranchZoom(zoom);
  }

  /**
   * Independent step-axis zoom, stretches the leaf-spacing (cross) axis, the
   * complement of the branch axis. Persisted prop, same contract as
   * {@link getBranchZoom}/{@link setBranchZoom}.
   */
  getStepZoom(): number {
    return this.instance?.getStepZoom() ?? 0;
  }

  setStepZoom(zoom: number): void {
    this.instance?.setStepZoom(zoom);
  }

  /**
   * Master leaf-label toggle used by the Tableau viz-settings path
   * (`app-tableau.ts` `labelType: 'none'`). Mirrors phylocanvas's
   * `{showLabels, showLeafLabels}` write, but routes through the adapter's
   * tracked state + master-gate derivation so it agrees with the appearance
   * panel's independent leaf/internal toggles.
   */
  toggleLabels(show: boolean): void {
    this.leafLabelsVisible = show;
    this.instance?.setProps(this.labelVisibilityProps());
  }

  // ---- appearance setters -------------------------------------------------
  //
  // These let the standalone app's shared appearance panel drive the SVG
  // renderer the way phylocanvas's embedded gear-menu drives phylocanvas. Each
  // independent control builds a one-key phylocanvas-vocabulary bag and reuses
  // the Phase A mapper, so the dialog and the panel share a single translation.
  //
  // The two label toggles are the exception: the library gates leaf and
  // internal labels behind a strict master `showLabels` (see
  // `labelVisibilityProps`), so they cannot be independent one-key props, a
  // naive `showLeafLabels: false` would also hide internal labels. Instead they
  // update tracked state and re-emit the full label trio, so the two toggles
  // move independently and the master gate stays correct.

  changeTreeLayout(code: string): void {
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ treeLayout: code }));
  }

  setNodeSize(size: number): void {
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ leafNodeSize: size }));
  }

  /**
   * Pick the leaf-marker shape. Unlike the size/layout setters, `nodeShape` has
   * no phylocanvas-vocabulary equivalent, so it is written straight to the
   * library prop rather than routed through `mapPhylocanvasSettingsToProps`. The
   * value is one of the library's shape ids (see the appearance panel's
   * `APPEARANCE_NODE_SHAPES`); a persisted appearance prop, so it round-trips
   * through the panel like node size.
   */
  setNodeShape(shape: string): void {
    this.instance?.setProps({ nodeShape: shape });
  }

  /**
   * Leaf-label font family. A native library prop (becomes the labels'
   * `font-family`) with no phylocanvas-vocabulary synonym, so it is written
   * straight like {@link setNodeShape}. The value is one of the panel's
   * `APPEARANCE_FONT_FAMILIES` codes (web-safe CSS generic families).
   */
  setFontFamily(family: string): void {
    this.instance?.setProps({ fontFamily: family });
  }

  /**
   * Leaf-label colour. The panel works in `#rrggbb`, but the library's
   * `fontColour` prop is an `[r,g,b,a]` integer array (its `normaliseColour`
   * silently renders a CSS string as black), so the hex is converted at this
   * boundary via {@link hexToRgba}, the single point where the panel's colour
   * vocabulary becomes the library's.
   */
  setFontColour(hex: string): void {
    this.instance?.setProps({ fontColour: hexToRgba(hex) });
  }

  /**
   * Set the highlight/selection halo ring colour. Like {@link setFontColour},
   * the library's `highlightColour` is an `[r,g,b,a]` array (and its alpha
   * channel drives the ring opacity), so the panel's hex is converted here via
   * {@link hexToRgba}.
   */
  setHaloColour(hex: string): void {
    this.instance?.setProps({ highlightColour: hexToRgba(hex) });
  }

  /** Set the halo ring radius (logical tree units); written straight. */
  setHaloRadius(radius: number): void {
    this.instance?.setProps({ haloRadius: radius });
  }

  /** Set the halo ring stroke width (logical tree units); written straight. */
  setHaloWidth(width: number): void {
    this.instance?.setProps({ haloWidth: width });
  }

  /**
   * Set the canvas padding (inset in px around the tree's drawing area); written
   * straight. The library subtracts `2 * padding` from the viewport before
   * scaling the tree, so larger values give the tree more edge clearance.
   */
  setPadding(padding: number): void {
    this.instance?.setProps({ padding });
  }

  /**
   * Set the metadata heat-map block edge length (px); written straight (the
   * library prop name `blockSize` has no phylocanvas synonym to route through).
   */
  setBlockSize(size: number): void {
    this.instance?.setProps({ blockSize: size });
  }

  /**
   * Set the extra scalebar decimal precision; written straight. Added to the
   * minimum digits `scalebarMetrics` computes from the branch scale, so a higher
   * value shows finer divisions on the bar's value label.
   */
  setScalebarDigits(digits: number): void {
    this.instance?.setProps({ scalebarDigits: digits });
  }

  /** Set the scalebar box width (px); written straight. */
  setScalebarWidth(width: number): void {
    this.instance?.setProps({ scalebarWidth: width });
  }

  /** Set the scalebar label font size (px); written straight. */
  setScalebarFontSize(size: number): void {
    this.instance?.setProps({ scalebarFontSize: size });
  }

  /** Set the scalebar background box fill-opacity (0…1); written straight. */
  setScalebarOpacity(opacity: number): void {
    this.instance?.setProps({ scalebarOpacity: opacity });
  }

  /**
   * Set the scalebar ink colour, the bar line (`scalebarStrokeColour`) and the
   * value label (`scalebarFillColour`) together, so the mark stays monochrome.
   * The `#rrggbb` string is converted to the library's RGBA array once here via
   * {@link hexToRgba}; the background box keeps its own colour and opacity.
   */
  setScalebarColour(hex: string): void {
    const ink = hexToRgba(hex);
    this.instance?.setProps({ scalebarFillColour: ink, scalebarStrokeColour: ink });
  }

  setLabelSize(size: number): void {
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ labelSize: size }));
  }

  toggleLabelAlignment(on: boolean): void {
    this.labelsAligned = on;
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ alignLabels: on }));
  }

  toggleColourEdges(on: boolean): void {
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ colourEdges: on }));
  }

  toggleLeafBorders(on: boolean): void {
    this.userLeafBorders = on;
    this.applyLeafBorders();
  }

  toggleScalebar(on: boolean): void {
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ scalebarVisible: on }));
  }

  /**
   * Toggle composition pie charts on collapsed internal nodes; written straight
   * (the library prop name `showPiecharts` has no phylocanvas synonym). When on,
   * `render/piecharts.js` draws a pie on each collapsed internal node summarising
   * the leaf-colour composition of the hidden subtree, so this pairs with the
   * collapse feature and the colour-by leaf fills.
   */
  togglePiecharts(on: boolean): void {
    this.instance?.setProps({ showPiecharts: on });
  }

  /**
   * Set branch-length decimal places. `_skipSave` exists only for call-site
   * signature parity with phylocanvas (which persists internally); SVG appearance
   * persistence runs through the panel/loadSettings path, like every sibling setter.
   */
  setDecimalDigits(digits: number, _skipSave = false): void {
    this.decimalDigits = digits;
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ decimalDigits: digits }));
  }

  toggleLeafLabels(on: boolean): void {
    this.leafLabelsVisible = on;
    this.instance?.setProps(this.labelVisibilityProps());
  }

  toggleInternalLabels(on: boolean): void {
    this.internalLabelsVisible = on;
    this.instance?.setProps(this.labelVisibilityProps());
  }

  /**
   * Internal-label font size, independent of the leaf-label size. A native
   * library prop (`internalLabelsFontSize`, which the renderer falls back from
   * to `fontSize ?? 16`); written straight like {@link setNodeShape}.
   */
  setInternalLabelSize(size: number): void {
    this.instance?.setProps({ internalLabelsFontSize: size });
  }

  /**
   * Numeric value filter for internal labels: only labels whose value lies in
   * `[min, max]` are drawn. The library's `internalLabelsFilterRange` is a
   * two-ended `[lo, hi]` array, so a half-open range is meaningless, this
   * collapses to `null` (no filter) unless *both* bounds are set. Passing
   * numbers (not strings) makes the library's `>=`/`<=` comparison numeric,
   * which is the intuitive behaviour for support values like bootstraps.
   */
  setInternalLabelFilter(min: number | null, max: number | null): void {
    const range = min != null && max != null ? [min, max] : null;
    this.instance?.setProps({ internalLabelsFilterRange: range });
  }

  /**
   * Serialise the live topology to a Newick string. Because the library re-hangs
   * its `source` on every reroot/midpoint-root/ladderise, the output tracks those
   * edits (parity with phylocanvas's "Export as Newick"). Empty before the first
   * render. Branch-length precision is the library's human-facing default (4
   * decimals), deliberately not the on-screen `decimalDigits`, which can be as
   * low as 1 and would silently truncate the exported file.
   */
  exportNewick(): string {
    return this.instance ? this.instance.exportNewick() : '';
  }

  /**
   * Serialise the live topology and trigger a browser download. The menu handler
   * for "Export Newick". DOM-guarded the same way the watermark is: it reaches
   * the window through the container's own document (`defaultView`) so it works
   * inside the Tableau iframe and no-ops cleanly on headless hosts (the test
   * runner, SSR) where `defaultView`/`URL`/`Blob` are absent.
   */
  downloadNewick(filename = 'tree.nwk'): void {
    const newick = this.exportNewick();
    if (!newick) return;
    this.triggerDownload(newick, filename, 'text/plain');
  }

  /**
   * Serialise the whole session, topology (loss-free 7-decimal Newick), props,
   * view and highlight, to a plain object (`exportJSON`). `null` before the
   * first render. The portable, round-trippable counterpart to `exportNewick`
   * (which is human-facing and lossy); restore it with `importSession`.
   */
  exportSession(): unknown {
    return this.instance ? this.instance.exportJSON() : null;
  }

  /**
   * Serialise the live session and trigger a browser download of a `.json` file.
   * The menu handler for "Save session"; DOM-guarded exactly like
   * `downloadNewick` (no-ops headless / outside a window).
   */
  downloadSession(filename = 'tree-session.json'): void {
    const session = this.exportSession();
    if (session == null) return;
    this.triggerDownload(JSON.stringify(session, null, 2), filename, 'application/json');
  }

  /**
   * Restore a session object produced by `exportSession`/`exportJSON`. The
   * library rebuilds the tree from the session's Newick and re-applies its props
   * (collapse, rotations, colours) and saved viewBox, so this re-syncs the
   * adapter's node maps to the rebuilt graph and *mirrors the restored collapse
   * set*, unlike a structural re-hang (reroot/ladderise), which clears it. A
   * display subtree the app was holding is released (the session defines its own
   * view). No-op before the first render or for a nullish session.
   */
  importSession(session: unknown): void {
    if (!this.instance || session == null) return;
    this.instance.importJSON(session);
    const hadSubtree = this.subtreeActive;
    this.subtreeActive = false;
    this.resyncGraphMaps();
    this.collapsedIds.clear();
    const props = (
      session as {
        props?: { collapsedIds?: unknown; annotations?: unknown; leafLabelAnnotations?: unknown };
      }
    ).props;
    const collapsed = props?.collapsedIds;
    if (Array.isArray(collapsed)) {
      for (const id of collapsed) this.collapsedIds.add(String(id));
    }
    this.mirrorNodeIds(this.annotatedNodeIds, props?.annotations);
    this.mirrorNodeIds(this.labelBandLeafIds, props?.leafLabelAnnotations);
    if (hadSubtree) this.emit('resetView', undefined);
  }

  /**
   * Rebuild a `nodeId` mirror Set from a restored session's annotation array.
   * Shared by the clade-annotation and leaf-label-band mirrors, which differ
   * only in which prop array feeds them.
   */
  private mirrorNodeIds(mirror: Set<string>, entries: unknown): void {
    mirror.clear();
    if (!Array.isArray(entries)) return;
    for (const entry of entries) {
      const nodeId = (entry as { nodeId?: unknown })?.nodeId;
      if (nodeId != null) mirror.add(String(nodeId));
    }
  }

  /**
   * Open a file picker, read a `.json` session and restore it (`importSession`).
   * The menu handler for "Load session". Reaches `FileReader` through the
   * container's own window (`defaultView`) so it works inside the Tableau iframe
   * and no-ops cleanly on headless hosts where no window is present.
   */
  loadSession(): void {
    const doc = this.container.ownerDocument;
    const view = doc?.defaultView as { FileReader?: typeof FileReader } | null | undefined;
    if (!doc || !view || typeof view.FileReader !== 'function') return;
    const input = doc.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new view.FileReader!();
      reader.onload = () => {
        try {
          this.importSession(JSON.parse(String(reader.result)));
        } catch {
          // Malformed session file, nothing safe to apply, leave the tree as-is.
        }
      };
      reader.readAsText(file);
    });
    input.click();
  }

  /** True when the internal node already carries a clade annotation. */
  hasCladeAnnotation(nodeId: string): boolean {
    return this.annotatedNodeIds.has(nodeId);
  }

  /**
   * Add a clade annotation (a coloured box) over the subtree rooted at an
   * internal node. The menu handler for "Annotate clade". Uses a deterministic
   * `clade-<nodeId>` id so "Remove annotation" can target it exactly, and a
   * palette colour (cycled by current count) so stacked boxes stay separable.
   * No-op for leaves, before the first render, or when the node is already
   * annotated. The annotation lives in the library's `annotations` prop, so it
   * rides session save/load for free.
   */
  annotateClade(nodeId: string): void {
    if (!this.instance || this.nodeById[nodeId]?.isLeaf !== false) return;
    if (this.annotatedNodeIds.has(nodeId)) return;
    const colour = cladeAnnotationColour(this.annotatedNodeIds.size);
    this.instance.createAnnotation({
      id: `clade-${nodeId}`,
      nodeId,
      fillColor: [...colour],
    });
    this.annotatedNodeIds.add(nodeId);
  }

  /**
   * Remove the clade annotation on an internal node. The menu handler for
   * "Remove annotation". No-op when the node has none or before the first render.
   */
  removeCladeAnnotation(nodeId: string): void {
    if (!this.instance || !this.annotatedNodeIds.has(nodeId)) return;
    this.instance.deleteAnnotation(`clade-${nodeId}`);
    this.annotatedNodeIds.delete(nodeId);
  }

  /** True when the leaf already carries a label-gutter band. */
  hasLeafLabelBand(nodeId: string): boolean {
    return this.labelBandLeafIds.has(nodeId);
  }

  /**
   * Add a coloured band in the label gutter beside a leaf. The menu handler for
   * "Annotate label", the leaf-level mirror of `annotateClade`. Uses a
   * deterministic `band-<nodeId>` id so "Remove label band" targets it exactly,
   * and a palette colour (cycled by current count) so adjacent bands stay
   * separable. No-op for internal nodes, before the first render, or when the
   * leaf already has a band. Lives in the library's `leafLabelAnnotations` prop,
   * so it rides session save/load for free.
   */
  annotateLeafLabel(nodeId: string): void {
    if (!this.instance || this.nodeById[nodeId]?.isLeaf !== true) return;
    if (this.labelBandLeafIds.has(nodeId)) return;
    const colour = cladeAnnotationColour(this.labelBandLeafIds.size);
    this.instance.createLeafLabelAnnotation({
      id: `band-${nodeId}`,
      nodeId,
      fillColor: [...colour],
    });
    this.labelBandLeafIds.add(nodeId);
  }

  /**
   * Remove the label-gutter band on a leaf. The menu handler for "Remove label
   * band". No-op when the leaf has none or before the first render.
   */
  removeLeafLabel(nodeId: string): void {
    if (!this.instance || !this.labelBandLeafIds.has(nodeId)) return;
    this.instance.deleteLeafLabelAnnotation(`band-${nodeId}`);
    this.labelBandLeafIds.delete(nodeId);
  }

  /**
   * Shared download primitive behind "Export Newick" and "Save session". Reaches
   * the window through the container's own document (`defaultView`) so it works
   * inside the Tableau iframe and no-ops cleanly on headless hosts (the test
   * runner, SSR) where `defaultView`/`URL`/`Blob` are absent.
   */
  private triggerDownload(text: string, filename: string, mimeType: string): void {
    const doc = this.container.ownerDocument;
    const view = doc?.defaultView as
      | { URL?: typeof URL; Blob?: typeof Blob }
      | null
      | undefined;
    if (
      !doc ||
      !view ||
      typeof view.URL?.createObjectURL !== 'function' ||
      typeof view.Blob !== 'function'
    ) {
      return;
    }
    const url = view.URL.createObjectURL(new view.Blob([text], { type: mimeType }));
    const link = doc.createElement('a');
    link.href = url;
    link.download = filename;
    (doc.body ?? this.container).appendChild(link);
    link.click();
    link.remove();
    view.URL.revokeObjectURL(url);
  }

  exportSVG(): string {
    return this.instance ? this.instance.exportSVG() : '';
  }

  exportPNG(scale = 1): Promise<string> {
    return this.instance
      ? this.instance.exportPNG(scale)
      : Promise.reject(new Error('exportPNG: renderer not yet rendered'));
  }

  addListener(event: string, callback: Listener): void {
    (this.listeners[event] ??= []).push(callback);
  }

  emit(event: string, data: unknown): void {
    this.listeners[event]?.forEach((cb) => cb(data));
  }

  destroy(): void {
    if (this.onContainerClick) {
      this.container.removeEventListener('click', this.onContainerClick);
      this.onContainerClick = null;
    }
    if (this.onContainerContextMenu) {
      this.container.removeEventListener('contextmenu', this.onContainerContextMenu);
      this.onContainerContextMenu = null;
    }
    if (this.onContainerMouseMove) {
      this.container.removeEventListener('mousemove', this.onContainerMouseMove);
      this.onContainerMouseMove = null;
    }
    this.closeContextMenu();
    this.teardownSelectionUI();
    this.watermarkEl?.remove();
    this.watermarkEl = null;
    this.instance?.destroy();
    this.instance = null;
  }

  // ---- internals ----------------------------------------------------------

  private buildInitialProps(): Record<string, unknown> {
    return {
      source: this.newick,
      size: { width: this.container.clientWidth, height: this.container.clientHeight },
      type: this.resolveLayout(),
      interactive: { highlight: true, tooltip: true },
      ...this.labelVisibilityProps(),
      alignLabels: this.labelsAligned,
      styleNodeEdges: true,
      selectedIds: this.selectedIds,
      styles: this.computeDisplayStyles(),
    };
  }

  /**
   * The label props the library needs, derived from one source of truth. The
   * library gates leaf/internal labels behind a master `showLabels`
   * (`render/labels.js`, `core/metadata.js`), so it must be (leaf OR internal)-* the same derivation `phylocanvas-renderer.ts` uses. Without the master gate
   * `showLeafLabels: true` alone renders no labels at all.
   */
  private labelVisibilityProps(): Record<string, unknown> {
    return {
      showLeafLabels: this.leafLabelsVisible,
      showInternalLabels: this.internalLabelsVisible,
      showLabels: this.leafLabelsVisible || this.internalLabelsVisible,
    };
  }

  private resolveLayout(): string {
    return resolveLayout();
  }

  private computeDisplayStyles(): Record<string, LibraryLeafStyle> {
    const merged: Record<string, LibraryLeafStyle> = { ...this.baseStyles };
    for (const [id, ls] of Object.entries(this.colorByStyles)) {
      merged[id] = { ...(merged[id] ?? {}), ...ls };
    }
    for (const [id, label] of Object.entries(this.labelOverrides)) {
      merged[id] = { ...(merged[id] ?? {}), label };
    }
    return filteredStyleOverrides(merged, this.leafIds, this.filteredTips);
  }

  /**
   * Drive the library's single global leaf-marker border from the effective
   * border state: the user's "Leaf borders" preference OR an active colour-by.
   * The vendored library paints one shared border for every marker (it never
   * reads per-node strokeColour for shapes), so colour-by switches the outline
   * on to keep adjacent same-fill tips legible; clearing colour-by falls back to
   * the user's preference. Width derivation stays in the shared settings mapper.
   */
  private applyLeafBorders(): void {
    const on = this.userLeafBorders || Object.keys(this.colorByStyles).length > 0;
    this.instance?.setProps(mapPhylocanvasSettingsToProps({ leafBorders: on }));
  }

  private pushStyles(): void {
    this.instance?.setProps({ styles: this.computeDisplayStyles() });
  }

  private mountWatermark(): void {
    if (this.watermarkEl) return;
    // Create through the container's own document so the overlay lands in the
    // right document (e.g. the Tableau extension iframe), and so non-DOM hosts
    // such as the test runner skip it instead of throwing on a missing global.
    const doc = this.container.ownerDocument;
    if (!doc) return;
    const img = doc.createElement('img');
    img.src = WATERMARK_SRC;
    img.alt = 'Theiagen Genomics';
    img.style.position = 'absolute';
    img.style.right = '12px';
    img.style.bottom = '12px';
    img.style.width = WATERMARK_WIDTH;
    img.style.height = 'auto';
    img.style.opacity = WATERMARK_OPACITY;
    img.style.pointerEvents = 'none';
    img.style.userSelect = 'none';
    this.container.appendChild(img);
    this.watermarkEl = img;
  }

  private attachClickListener(): void {
    this.onContainerClick = (e: Event) => {
      const target = e.target as Element | null;
      const hit =
        target && typeof target.closest === 'function'
          ? target.closest('[data-node-id]')
          : null;
      if (!hit) return;
      const id = hit.getAttribute('data-node-id');
      if (id == null) return;
      this.emit('click', { node: { id, isLeaf: this.leafIds.has(id) } });
    };
    this.container.addEventListener('click', this.onContainerClick);
  }

  // Enrich the leaf hover tooltip with the leaf's metadata values, matching
  // phylocanvas's node tooltip. The vendored library hardcodes its own hover
  // handler (it shows just the leaf label) on the inner `svg`; this observer
  // rides the same `mousemove` on the *container* -- an ancestor -- so during
  // event bubbling it fires last and its `setTooltip` wins the native title.
  // Metadata cells (which carry `data-col`) keep the library's `field: value`
  // tooltip; leaves with no blocks keep the library's plain label. Guarded for
  // non-DOM hosts: with no owner document the listener is never attached.
  private attachTooltipListener(): void {
    if (!this.container.ownerDocument) return;
    this.onContainerMouseMove = (e: Event) => {
      const target = (e as { target?: Element | null }).target ?? null;
      const hit =
        target && typeof target.closest === 'function'
          ? target.closest('[data-node-id]')
          : null;
      const id = hit?.getAttribute('data-node-id') ?? null;
      const col = hit?.getAttribute('data-col') ?? null;
      if (id == null || col != null) return;
      const row = this.blockMetadata[id];
      if (!row) return;
      this.instance?.setTooltip(buildTooltipText(id, this.tooltipFields, row));
    };
    this.container.addEventListener('mousemove', this.onContainerMouseMove);
  }

  // Right-click navigation menu, parity with phylocanvas's "View subtree" /
  // "Reset view". Self-contained and guarded for non-DOM hosts (the test
  // runner, SSR): if the container has no owner document the listener is never
  // attached, so headless hosts stay completely untouched. The decision of
  // which entries to show lives in the pure `subtreeMenuItems`; this method only
  // does the DOM plumbing and dispatch.
  private attachContextMenuListener(): void {
    if (!this.container.ownerDocument) return;
    this.onContainerContextMenu = (e: Event) => {
      const node = (e as { target?: Element | null }).target ?? null;
      const hit =
        node && typeof node.closest === 'function' ? node.closest('[data-node-id]') : null;
      const id = hit?.getAttribute('data-node-id') ?? null;
      const menuTarget: MenuTarget | null =
        id != null
          ? {
              id,
              isInternal: this.nodeById[id]?.isLeaf === false,
              isCollapsed: this.collapsedIds.has(id),
              isRoot: id === this.rootId,
              hasAnnotation: this.annotatedNodeIds.has(id),
              hasLabelBand: this.labelBandLeafIds.has(id),
            }
          : null;
      const items = subtreeMenuItems(menuTarget, {
        subtreeActive: this.subtreeActive,
        anyCollapsed: this.hasCollapsed(),
        canMidpointRoot: this.leafIds.size >= 2,
        canLadderise: this.leafIds.size >= 2,
        canExport: this.leafIds.size >= 1,
        canSession: this.leafIds.size >= 1,
        canSelect: this.leafIds.size >= 1,
      });
      if (items.length === 0) return; // nothing to offer, leave the native menu
      if (typeof (e as { preventDefault?: () => void }).preventDefault === 'function') {
        (e as { preventDefault: () => void }).preventDefault();
      }
      this.openContextMenu(items, e, menuTarget);
    };
    this.container.addEventListener('contextmenu', this.onContainerContextMenu);
  }

  private openContextMenu(
    items: SubtreeMenuItem[],
    e: Event,
    target: MenuTarget | null,
  ): void {
    const doc = this.container.ownerDocument;
    if (!doc) return;
    this.closeContextMenu();

    const menu = doc.createElement('div');
    menu.setAttribute('data-role', 'subtree-menu');
    const pos = e as { clientX?: number; clientY?: number };
    Object.assign(menu.style, {
      position: 'absolute',
      left: `${pos.clientX ?? 0}px`,
      top: `${pos.clientY ?? 0}px`,
      zIndex: '1000',
      minWidth: '140px',
      padding: '4px 0',
      background: '#fff',
      border: '1px solid #d0d0d0',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      font: '13px sans-serif',
      userSelect: 'none',
    });

    for (const def of items) {
      const item = doc.createElement('div');
      item.textContent = def.label;
      item.setAttribute('data-action', def.id);
      Object.assign(item.style, { padding: '6px 14px', cursor: 'pointer' });
      item.addEventListener('click', () => {
        this.closeContextMenu();
        if (def.id === 'view-subtree' && target) this.viewSubtree(target.id);
        else if (def.id === 'collapse' && target) this.collapseNode(target.id);
        else if (def.id === 'expand' && target) this.expandNode(target.id);
        else if (def.id === 'rotate' && target) this.rotateNode(target.id);
        else if (def.id === 'reroot' && target) this.rerootNode(target.id);
        else if (def.id === 'annotate-clade' && target) this.annotateClade(target.id);
        else if (def.id === 'remove-annotation' && target) this.removeCladeAnnotation(target.id);
        else if (def.id === 'annotate-leaf' && target) this.annotateLeafLabel(target.id);
        else if (def.id === 'remove-leaf-band' && target) this.removeLeafLabel(target.id);
        else if (def.id === 'midpoint-root') this.midpointRoot();
        else if (def.id === 'ladderise-asc') this.ladderiseAscending();
        else if (def.id === 'ladderise-desc') this.ladderiseDescending();
        else if (def.id === 'export-newick') this.downloadNewick();
        else if (def.id === 'save-session') this.downloadSession();
        else if (def.id === 'load-session') this.loadSession();
        else if (def.id === 'expand-all') this.expandNode();
        else if (def.id === 'reset-view') this.resetSubtreeView();
        else if (def.id === 'lasso-select') this.setSelectMode('lasso');
        else if (def.id === 'box-select') this.setSelectMode('box');
      });
      menu.appendChild(item);
    }

    this.container.appendChild(menu);
    this.contextMenuEl = menu;

    // Dismiss on any click outside the menu.
    this.onDocMouseDown = (me: Event) => {
      const t = (me as { target?: unknown }).target;
      if (t === menu) return;
      if (typeof menu.contains === 'function' && menu.contains(t as Node)) return;
      this.closeContextMenu();
    };
    doc.addEventListener('mousedown', this.onDocMouseDown);
  }

  private closeContextMenu(): void {
    if (this.contextMenuEl) {
      this.contextMenuEl.remove();
      this.contextMenuEl = null;
    }
    if (this.onDocMouseDown) {
      this.container.ownerDocument?.removeEventListener('mousedown', this.onDocMouseDown);
      this.onDocMouseDown = null;
    }
  }

  // ---- Lasso / box selection ----------------------------------------------
  // A floating toolbar (Pan / Lasso / Box) plus a transparent overlay layered
  // over the library's svg. In pan mode the overlay lets events through so the
  // library's own pan/zoom/click keep working; in lasso/box mode it captures the
  // drag, suspending pan. The enclosed tips are emitted on `lasso-select`-// exactly the event app.ts already converts to table + Tableau-mark selection.
  // Guarded for non-DOM hosts: with no owner document nothing is mounted.
  private mountSelectionUI(): void {
    const doc = this.container.ownerDocument;
    // Needs the full element API (namespaced svg, setAttribute, listeners). Like
    // the watermark, non-DOM hosts (the test runner's minimal stubs) skip it
    // instead of throwing on a missing method.
    if (!doc || typeof doc.createElementNS !== 'function') return;

    const overlay = doc.createElement('div');
    overlay.setAttribute('data-role', 'selection-overlay');
    Object.assign(overlay.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '5',
      pointerEvents: 'none',
      cursor: 'crosshair',
    });
    const ns = 'http://www.w3.org/2000/svg';
    const feedback = doc.createElementNS(ns, 'svg');
    feedback.setAttribute('data-role', 'selection-feedback');
    Object.assign((feedback as unknown as { style: CSSStyleDeclaration }).style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
    });
    overlay.appendChild(feedback);
    this.container.appendChild(overlay);
    this.selectOverlayEl = overlay;
    this.selectFeedbackEl = feedback;

    // Lasso/box selection is armed from the right-click context menu, not a
    // toolbar. Once armed, one completed gesture selects and snaps back to pan
    // (see handleSelectPointerUp); Esc cancels mid-gesture.
    this.onSelectPointerDown = (e: PointerEvent) => this.handleSelectPointerDown(e);
    this.onSelectPointerMove = (e: PointerEvent) => this.handleSelectPointerMove(e);
    this.onSelectPointerUp = (e: PointerEvent) => this.handleSelectPointerUp(e);
    overlay.addEventListener('pointerdown', this.onSelectPointerDown);
    overlay.addEventListener('pointermove', this.onSelectPointerMove);
    overlay.addEventListener('pointerup', this.onSelectPointerUp);

    this.onSelectKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.setSelectMode('pan');
    };
    doc.addEventListener('keydown', this.onSelectKeyDown);

    this.applySelectMode();
  }

  /** Switch interaction mode; clears any in-flight gesture and feedback. */
  setSelectMode(mode: 'pan' | 'lasso' | 'box'): void {
    this.selectMode = mode;
    this.selectDrawing = false;
    this.selectPoints = [];
    this.clearFeedback();
    this.applySelectMode();
  }

  private applySelectMode(): void {
    if (this.selectOverlayEl) {
      // In pan mode the overlay is click-through; arming select captures pointers.
      this.selectOverlayEl.style.pointerEvents = this.selectMode === 'pan' ? 'none' : 'auto';
    }
  }

  private handleSelectPointerDown(e: PointerEvent): void {
    if (this.selectMode === 'pan') return;
    e.preventDefault();
    this.selectDrawing = true;
    this.selectAdditive = e.ctrlKey || e.metaKey;
    this.selectPoints = [{ x: e.clientX, y: e.clientY }];
    // Capture keeps moves/up flowing even if the pointer leaves the overlay.
    // Best-effort: with no active pointer (e.g. a synthetic event) it throws,
    // and a failed capture must not abort the gesture.
    const target = e.target as Element & { setPointerCapture?: (id: number) => void };
    try {
      target?.setPointerCapture?.(e.pointerId);
    } catch {
      /* pointer not capturable, proceed without capture */
    }
  }

  private handleSelectPointerMove(e: PointerEvent): void {
    if (!this.selectDrawing) return;
    const pt = { x: e.clientX, y: e.clientY };
    const start = this.selectPoints[0];
    if (this.selectMode === 'lasso') this.selectPoints.push(pt);
    else if (start) this.selectPoints = [start, pt];
    this.drawFeedback();
  }

  private handleSelectPointerUp(e: PointerEvent): void {
    if (!this.selectDrawing) return;
    this.selectDrawing = false;
    void e;
    const shape = this.buildShape();
    this.clearFeedback();
    this.selectPoints = [];
    if (shape) {
      this.finishSelection(shape);
      // One-shot: a completed gesture snaps back to pan so the menu is the sole
      // way to re-arm. A degenerate click (no shape) keeps the mode for a retry.
      this.setSelectMode('pan');
    }
  }

  /**
   * Turn the captured client-space points into a selection shape, or null for a
   * degenerate gesture (a click, span < 3px) so a stray click never wipes the
   * current selection.
   */
  private buildShape(): SelectionShape | null {
    const pts = this.selectPoints;
    if (pts.length < 2) return null;
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const span = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
    );
    if (span < 3) return null;
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (!first || !last) return null;
    if (this.selectMode === 'box') {
      return { kind: 'box', rect: normalizeRect(first, last) };
    }
    return { kind: 'lasso', points: pts };
  }

  /** Project every leaf to client space, hit-test, merge, and announce. */
  private finishSelection(shape: SelectionShape): void {
    // Union two hit-source domains: each tip's branch-end point AND its visible
    // label centre. Labels render well to the right of the branch ends, so a
    // gesture drawn around the labels must still select their tips. tipsInShape
    // dedupes a tip caught by both.
    const hits = tipsInShape([...this.projectTips(), ...this.projectTipLabels()], shape);
    const next = mergeSelection(this.selectedIds, hits, this.selectAdditive);
    this.emit('lasso-select', { leafIds: next, source: shape.kind === 'box' ? 'box' : 'lasso' });
  }

  /**
   * Each leaf's world coordinates projected to client (screen) space, read fresh
   * so the projection reflects the live pan/zoom. The library's svg fills the
   * container, so the container's bounding rect is the projection rect.
   */
  private projectTips(): TipPoint[] {
    const instance = this.instance;
    if (!instance) return [];
    const rect = this.container.getBoundingClientRect();
    const view = instance.getView();
    const tips: TipPoint[] = [];
    for (const id of this.leafIds) {
      const node = instance.findNodeById(id);
      if (!node) continue;
      const [x, y] = worldToClient(view, rect, [node.x, node.y]) as [number, number];
      tips.push({ id, x, y });
    }
    return tips;
  }

  /**
   * Centre point (client space) of every rendered leaf label. The library tags
   * each leaf label `<text>` with `data-node-id`; label thinning means only the
   * visible subset is present, which is exactly what the user can lasso. Read
   * straight off the DOM so positions reflect the live pan/zoom and layout.
   */
  private projectTipLabels(): TipPoint[] {
    const svg = this.container.querySelector('svg');
    if (!svg) return [];
    const labels: TipPoint[] = [];
    svg.querySelectorAll('text[data-node-id]').forEach((el) => {
      const id = el.getAttribute('data-node-id');
      if (!id || !this.leafIds.has(id)) return;
      const r = (el as SVGGraphicsElement).getBoundingClientRect();
      labels.push({ id, x: r.left + r.width / 2, y: r.top + r.height / 2 });
    });
    return labels;
  }

  private drawFeedback(): void {
    const fb = this.selectFeedbackEl;
    const overlay = this.selectOverlayEl;
    const doc = this.container.ownerDocument;
    if (!fb || !overlay || !doc || this.selectPoints.length === 0) return;
    this.clearFeedback();
    const rect = overlay.getBoundingClientRect();
    const ns = 'http://www.w3.org/2000/svg';
    const stroke = '#2962ff';
    const fill = 'rgba(41,98,255,0.12)';
    const first = this.selectPoints[0];
    const last = this.selectPoints[this.selectPoints.length - 1];
    if (!first || !last) return;
    if (this.selectMode === 'box') {
      const r = normalizeRect(first, last);
      const el = doc.createElementNS(ns, 'rect');
      el.setAttribute('x', String(r.x - rect.left));
      el.setAttribute('y', String(r.y - rect.top));
      el.setAttribute('width', String(r.w));
      el.setAttribute('height', String(r.h));
      el.setAttribute('fill', fill);
      el.setAttribute('stroke', stroke);
      el.setAttribute('stroke-dasharray', '4 3');
      fb.appendChild(el);
    } else {
      const el = doc.createElementNS(ns, 'polyline');
      const local = this.selectPoints.map((p) => `${p.x - rect.left},${p.y - rect.top}`).join(' ');
      el.setAttribute('points', local);
      el.setAttribute('fill', fill);
      el.setAttribute('stroke', stroke);
      el.setAttribute('stroke-dasharray', '4 3');
      fb.appendChild(el);
    }
  }

  private clearFeedback(): void {
    const fb = this.selectFeedbackEl;
    if (!fb) return;
    while (fb.firstChild) fb.removeChild(fb.firstChild);
  }

  private teardownSelectionUI(): void {
    const overlay = this.selectOverlayEl;
    if (overlay) {
      if (this.onSelectPointerDown) overlay.removeEventListener('pointerdown', this.onSelectPointerDown);
      if (this.onSelectPointerMove) overlay.removeEventListener('pointermove', this.onSelectPointerMove);
      if (this.onSelectPointerUp) overlay.removeEventListener('pointerup', this.onSelectPointerUp);
      overlay.remove();
    }
    if (this.onSelectKeyDown) {
      this.container.ownerDocument?.removeEventListener('keydown', this.onSelectKeyDown);
    }
    this.selectOverlayEl = null;
    this.selectFeedbackEl = null;
    this.onSelectPointerDown = null;
    this.onSelectPointerMove = null;
    this.onSelectPointerUp = null;
    this.onSelectKeyDown = null;
  }
}
